import type { CreateNeighborhoodNotification } from "@/dtos/CreateNeighborhoodNotification";
import type { CreateNotificationDto } from "@/dtos/CreateNotificationDto";
import type { CreateGeneralNotificationDto } from "@/dtos/CreateGeneralNotificationDto";
import api from "@/services/api";
import { useMutation, useQuery } from "@tanstack/react-query";

interface UseGetNotificationsParams {
  page?: number;
  limit?: number;
  title?: string;
  type?: string;
}

export const useGetNotifications = (params: UseGetNotificationsParams = {}) => {
  const { page = 1, limit = 10, title, type } = params;

  return useQuery({
    queryKey: ["notifications", params],
    queryFn: async () => {
      const queryParams: Record<string, any> = { page, limit };

      if (title) queryParams.title = title;
      if (type) queryParams.type = type;

      const response = await api.get("/notifications/admin/all", {
        params: queryParams,
      });

      return response.data;
    },
  });
};

export const useCreateNotification = () => {
  return useMutation({
    mutationKey: ["create-notification"],
    mutationFn: async (createNotificationDto: CreateNotificationDto) => {
      const response = await api.post("/notifications", createNotificationDto);

      return response.data;
    },
  });
};

export const useCreateNeighborhoodNotification = () => {
  return useMutation({
    mutationKey: ["create-neighborhood-notification"],
    mutationFn: async (
      createNeighborhoodNotification: CreateNeighborhoodNotification,
    ) => {
      const response = await api.post(
        "/notifications/announcements/neighborhood",
        createNeighborhoodNotification,
      );

      return response.data;
    },
  });
};

export const useCreateGeneralNotification = () => {
  return useMutation({
    mutationKey: ["create-general-notification"],
    mutationFn: async (
      createGeneralNotification: CreateGeneralNotificationDto,
    ) => {
      const response = await api.post(
        "/notifications/announcements/general",
        createGeneralNotification,
      );

      return response.data;
    },
  });
};

export const useGetNotificationById = (id: string) => {
  return useQuery({
    queryKey: ["notification", id],
    queryFn: async () => {
      const response = await api.get(`/notifications/${id}`);
      return response.data;
    },
  });
};

export const useDeleteNotification = () => {
  return useMutation({
    mutationKey: ["delete-notification"],
    mutationFn: async (id: string) => {
      const response = await api.delete(`/notifications/${id}`);

      return response.data;
    },
  });
};
